/**
 * AlertBell — sino de ALERTAS pro painel GM (admin).
 * Igual NotificationBell mas usa API admin (gm_alert, crash, dup_detected, etc).
 * Ícone diferente (ShieldAlert) pra distinguir do sino do player.
 */
import { useEffect, useRef, useState } from 'react';
import { ShieldAlert, AlertTriangle, Bug, Ban, ShieldCheck, Filter, HandCoins, MessageCircle, LifeBuoy } from 'lucide-react';
import { NotificationsAPI, NotificationItem } from './api';

const POLL_INTERVAL_MS = 20_000;   // GM polla mais rapido que player
const PAGE_SIZE = 12;

function fmtTime(iso: string) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('pt-BR');
}

function kindIcon(kind: string) {
  switch (kind) {
    case 'crash': return <Bug className="w-4 h-4 text-orange-500" />;
    case 'autoban_log':
    case 'dup_detected': return <Ban className="w-4 h-4 text-red-500" />;
    case 'security': return <ShieldAlert className="w-4 h-4 text-purple-500" />;
    case 'rmt': return <HandCoins className="w-4 h-4 text-amber-400" />;
    case 'bot_whisper': return <MessageCircle className="w-4 h-4 text-purple-300" />;
    case 'ticket': return <LifeBuoy className="w-4 h-4 text-sky-400" />;
    default: return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
  }
}

function priorityAccent(p: number) {
  return p === 1 ? 'border-l-red-600' :
         p === 2 ? 'border-l-orange-500' :
         p === 3 ? 'border-l-yellow-500' :
                   'border-l-gray-500';
}

export function AlertBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [kindFilter, setKindFilter] = useState('');
  const [priorityMax, setPriorityMax] = useState<number | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  // Badge "congelado" com o valor que tinha na hora da abertura — enquanto o
  // popup fica aberto o botao segue mostrando esse numero (frescura de UX).
  const [openedBadge, setOpenedBadge] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Poll do contador (stats endpoint dá total_unread_gm)
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await NotificationsAPI.adminStats();
        if (alive) setUnread(r.totalUnreadGm);
      } catch {}
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function loadList(p = 1, append = false) {
    setLoading(true);
    try {
      const r = await NotificationsAPI.adminList(
        p, PAGE_SIZE,
        kindFilter || undefined,
        typeof priorityMax === 'number' ? priorityMax : undefined,
      );
      setItems(append ? [...items, ...r.items] : r.items);
      setUnread(r.unread);
      setTotal(r.total);
      setHasMore(p * PAGE_SIZE < r.total);
      setPage(p);
    } finally {
      setLoading(false);
    }
  }

  // Recarrega quando muda filtro (se dropdown aberto)
  useEffect(() => {
    if (open) loadList(1, false);
    // eslint-disable-next-line
  }, [kindFilter, priorityMax]);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    if (loading || !hasMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadList(page + 1, true);
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      setOpenedBadge(unread);        // congela o badge no valor de abertura
      loadList(1, false);
      // Abrir o popup ja zera o badge (UX estilo Facebook): marca todos os
      // alertas de GM como lidos pra este GM (junction), sem aguardar resposta.
      if (unread > 0) {
        const nowIso = new Date().toISOString();
        setUnread(0);
        setItems(prev => prev.map(i => i.readAt ? i : { ...i, readAt: nowIso }));
        NotificationsAPI.adminReadAll().catch(() => {
          // se falhar, o proximo poll do stats restaura o contador real
        });
      }
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={toggle}
        className={`relative inline-flex items-center justify-center w-10 h-10 rounded-full transition cursor-pointer ${open ? 'bg-white/15 ring-1 ring-red-400/40' : 'hover:bg-white/10'}`}
        aria-label="Alertas"
        title="Alertas"
      >
        <ShieldAlert className="w-5 h-5 text-red-400" />
        {(() => {
          // Enquanto aberto, segura o badge no valor da abertura (openedBadge).
          const badge = open ? openedBadge : unread;
          return badge > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
              {badge > 99 ? '99+' : badge}
            </span>
          ) : null;
        })()}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[199]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[380px] max-w-[95vw] bg-[#161e26] border border-red-500/30
                          rounded-lg shadow-2xl overflow-hidden z-[200]">
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-red-900/40 to-[#0f1418]">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-400" />
              <div className="font-semibold text-white">Alertas</div>
              <span className="text-[10px] text-white/40">({total})</span>
            </div>
            <button
              onClick={() => setShowFilters(v => !v)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs cursor-pointer ${showFilters ? 'bg-red-500/20 text-red-300' : 'text-white/55 hover:text-white/80'}`}
            >
              <Filter className="w-3.5 h-3.5" /> Filtros
            </button>
          </div>

          {/* Filtros (toggleable) */}
          {showFilters && (
            <div className="px-3 py-2 border-b border-white/10 bg-[#0f1418] flex gap-2 flex-wrap">
              <select
                value={kindFilter}
                onChange={e => setKindFilter(e.target.value)}
                className="flex-1 min-w-[120px] px-2 py-1 bg-[#1a242d] border border-white/10 rounded text-xs text-white"
              >
                <option value="">Todos kinds</option>
                <option value="crash">Crash</option>
                <option value="autoban_log">Autoban</option>
                <option value="dup_detected">Dup</option>
                <option value="gm_alert">GM Alert</option>
                <option value="security">Security</option>
                <option value="ticket">Tickets</option>
                <option value="rmt">RMT</option>
                <option value="bot_whisper">Sussurro de bot</option>
                <option value="broadcast">Broadcast</option>
              </select>
              <select
                value={priorityMax === '' ? '' : String(priorityMax)}
                onChange={e => setPriorityMax(e.target.value ? Number(e.target.value) : '')}
                className="flex-1 min-w-[120px] px-2 py-1 bg-[#1a242d] border border-white/10 rounded text-xs text-white"
              >
                <option value="">Todas prioridades</option>
                <option value="1">Apenas urgente</option>
                <option value="2">Alta+</option>
                <option value="3">Média+</option>
              </select>
            </div>
          )}

          {/* Lista com scroll infinito */}
          <div onScroll={onScroll} className="max-h-[440px] overflow-y-auto">
            {items.length === 0 && !loading && (
              <div className="px-4 py-8 text-center text-sm text-white/55 flex flex-col items-center gap-2">
                <ShieldCheck className="w-10 h-10 text-green-500/50" />
                <span>Sem alertas ativos.</span>
              </div>
            )}
            {items.map(n => (
              <div
                key={n.id}
                className={`px-3 py-2.5 border-l-4 ${priorityAccent(n.priority)}
                            ${n.readAt ? 'opacity-55' : 'bg-white/[0.02]'}
                            hover:bg-white/[0.04] transition border-b border-white/5
                            flex items-start justify-between gap-2`}
              >
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {kindIcon(n.kind)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <code className="text-[10px] text-white/45">{n.kind}</code>
                      {n.title && <span className="text-xs font-semibold text-white truncate">{n.title}</span>}
                      {!n.readAt && <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0"></span>}
                    </div>
                    <div className="text-xs text-white/80 line-clamp-2">{n.message}</div>
                    {(n.targetCharIdx || n.targetUserNum) && (
                      <div className="text-[10px] text-white/45 mt-0.5">
                        {n.targetCharIdx ? `char ${n.targetCharIdx}` : `user ${n.targetUserNum}`}
                        {n.source && ` · ${n.source}`}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-[10px] text-white/40 whitespace-nowrap flex-shrink-0">{fmtTime(n.createdAt)}</div>
              </div>
            ))}
            {loading && (
              <div className="px-4 py-3 text-center text-xs text-white/55">Carregando…</div>
            )}
            {!hasMore && items.length > 0 && !loading && (
              <div className="px-4 py-3 text-center text-[10px] text-white/30">— fim ({total} total) —</div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
